import { jwtDecode } from 'jwt-decode'
import React from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import {
  resetAppConfig,
  setDefaultAgent,
  setRoom,
} from '@store/actionSlices/appConfig'
import {
  removeProjectIdentity,
  setProjectIdentity,
} from '@store/actionSlices/projectIdentity'
import { removeShortlist } from '@store/actionSlices/shortlist'
import { removeToken, setToken } from '@store/actionSlices/token'
import { removeUser, setUser } from '@store/actionSlices/user'
import { removeWhiteLabel } from '@store/actionSlices/whiteLabel'
import { DEFAULT_SHOWCASE_ROOM, RouteKey } from '@store/constants'
import { ProjectIdentity, SessionListContent } from '@store/types'

import { Auth, TokenUserPayloadInterface } from '@api/authentication'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import { notifyError } from '@adUtilities/notifier'

import { removeOfflineResponseFromLocalStorage } from './offline-handler-util'
import ResetUtil from './reset-util'

const ISSUER = 'SGC'

interface SSOProps {
  projectIdentity: ProjectIdentity
  accessToken?: string
}

interface SSOData extends Auth {
  issuer: string
}

function SSO({ projectIdentity, accessToken }: SSOProps): void {
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const resetUtil = ResetUtil()

  const dispatch = useDispatch()
  const history = useHistory()

  const disconnecSessionFromFirebase = async (
    sessionList: Array<SessionListContent>
  ) => {
    await firebaseControlQuery.refreshSession()
    await Promise.all(
      sessionList.map(async ({ sessionId, projectId }) => {
        await firebaseControlQuery.updateConnection({
          connected: false,
          projectId,
          sessionId,
        })
      })
    )
  }

  const resetPreviousUserSession = async () => {
    dispatch(removeToken())
    dispatch(removeProjectIdentity())
    dispatch(removeUser())
    dispatch(resetAppConfig())
    dispatch(removeWhiteLabel())
    dispatch(removeShortlist())
    removeOfflineResponseFromLocalStorage()
    resetUtil.resetAllApiState()
  }

  const processSSO = async (data: SSOData) => {
    if (Object.keys(data).length === 0) {
      notifyError('Invalid token.')
      return
    }

    const projectCanonicalName = data?.project?.canonical_name || ''
    const projectId = data?.project?.id || ''
    const projectName = data?.project?.name || ''
    const user = data?.user || ({} as TokenUserPayloadInterface)
    const showcaseRoom = data?.showcaseRoom || null
    const issuer = data?.issuer || ''

    if (
      !projectCanonicalName ||
      !projectId ||
      !projectName ||
      Object.keys(user).length === 0 ||
      issuer !== ISSUER
    ) {
      notifyError('Invalid token.')
      return
    }

    const { sessionList = [] } = projectIdentity

    if (sessionList.length > 0) {
      await disconnecSessionFromFirebase(sessionList)
    }

    await resetPreviousUserSession()

    dispatch(setDefaultAgent(true))

    dispatch(setRoom(showcaseRoom?.name || DEFAULT_SHOWCASE_ROOM))

    const {
      access_token,
      refresh_token,
      name: fullName,
      email,
      _id: userId,
    } = user

    dispatch(setToken({ refresh_token, access_token, email, _id: userId }))

    dispatch(
      setProjectIdentity({
        ...projectIdentity,
        projectUUID: projectId,
        projectName: projectCanonicalName,
        projectId: projectCanonicalName,
        projectLabel: projectName,
        sessionId: '',
        sessionList: [],
        masterKey: '',
      })
    )

    dispatch(
      setUser({
        name: fullName,
        email,
      })
    )
  }

  React.useEffect(() => {
    if (!accessToken) {
      return
    }

    try {
      const parsedToken: {
        payload: { data: SSOData }
      } = jwtDecode(accessToken)
      processSSO(parsedToken?.payload?.data || {}).finally(() =>
        history.push(`/${RouteKey.DASHBOARD}`)
      )
    } catch {
      notifyError('Invalid token.')
      history.push(`/${RouteKey.DASHBOARD}`)
    }
  }, [accessToken])
}

export default SSO
